import { toast } from 'sonner';

// Share an invoice PDF via the Web Share API with iOS-safe gesture handling.
//
// iOS/Safari revokes the user gesture ("transient activation") while the PDF
// is being fetched, making navigator.share throw NotAllowedError. We cache the
// fetched File so the follow-up tap shares instantly (still inside its own
// gesture), instead of forcing a download.
const pdfCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;

export async function shareInvoicePdf({ cacheKey, fileName, title, text, url, getBlob, downloadFallback }) {
  if (!navigator.share) {
    try {
      await downloadFallback();
    } catch {
      toast.error('Unable to share or download. Please use the Download PDF option.');
    }
    return;
  }

  try {
    let file = pdfCache.get(cacheKey);
    if (!file) {
      toast.loading('Preparing invoice for sharing…', { id: 'share-loading' });
      const blob = await getBlob();
      file = new File([blob], fileName, { type: 'application/pdf' });
      pdfCache.set(cacheKey, file);
      setTimeout(() => pdfCache.delete(cacheKey), CACHE_TTL_MS);
      toast.dismiss('share-loading');
    }

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ title, text, files: [file] });
      toast.success('Invoice shared successfully!');
    } else {
      await navigator.share({ title, text, url });
      toast.success('Invoice details shared!');
    }
  } catch (error) {
    toast.dismiss('share-loading');
    if (error && error.name === 'AbortError') return; // user cancelled
    if (error && error.name === 'NotAllowedError') {
      // Gesture expired during the fetch - PDF is cached, next tap shares instantly.
      toast.info('Your invoice is ready: tap Share once more to open the share menu.', {
        id: 'share-retry',
        duration: 5000,
      });
      return;
    }
    console.error('Share error:', error);
    toast.info('Sharing was blocked by your browser: downloading the PDF instead…');
    try {
      await downloadFallback();
    } catch {
      toast.error('Unable to share or download. Please use the Download PDF option.');
    }
  }
}
